import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { generateDataStrategyPDF } from '../../../utils/roiPdfGenerator';
import { useAuth } from '../../../contexts/AuthContext';
import { assessmentService } from '../../../services/assessmentService';

const DataStrategyCalculator = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companySize: '',
    industry: '',
    currentDataMaturity: '',
    annualRevenue: '',
    dataTeamSize: '',
    currentDataCosts: '',
    decisionMakingDelay: '',
    dataQualityIssues: '',
    complianceRequirements: '',
    contactInfo: {
      name: '',
      email: '',
      company: '',
      role: ''
    }
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Enterprise (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Professional Services' },
    { value: 'other', label: 'Other' }
  ];

  const maturityOptions = [
    { value: 'basic', label: 'Basic - Spreadsheets & Manual Processes' },
    { value: 'developing', label: 'Developing - Some BI Tools & Dashboards' },
    { value: 'defined', label: 'Defined - Structured Data Processes' },
    { value: 'managed', label: 'Managed - Advanced Analytics Capabilities' },
    { value: 'optimized', label: 'Optimized - AI/ML & Predictive Analytics' }
  ];

  const calculateROI = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const revenue = parseFloat(formData?.annualRevenue) || 0;
      const currentCosts = parseFloat(formData?.currentDataCosts) || 0;
      const teamSize = parseInt(formData?.dataTeamSize) || 0;
      
      // ROI calculation logic based on industry benchmarks
      const efficiencyGains = revenue * 0.15; // 15% efficiency improvement
      const decisionSpeedUp = revenue * 0.08; // 8% from faster decisions
      const riskReduction = revenue * 0.05; // 5% risk mitigation
      const costSavings = currentCosts * 0.25; // 25% cost optimization
      
      const totalBenefits = efficiencyGains + decisionSpeedUp + riskReduction + costSavings;
      const implementationCost = teamSize * 50000 + 100000; // Base implementation cost
      const annualROI = ((totalBenefits - implementationCost) / implementationCost) * 100;
      const paybackPeriod = implementationCost / (totalBenefits / 12);

      setResults({
        totalBenefits: totalBenefits,
        implementationCost: implementationCost,
        annualROI: Math.max(annualROI, 0),
        paybackPeriod: Math.min(paybackPeriod, 24),
        efficiencyGains: efficiencyGains,
        decisionSpeedUp: decisionSpeedUp,
        riskReduction: riskReduction,
        costSavings: costSavings,
        monthlyBenefit: totalBenefits / 12
      });
      
      setIsCalculating(false);
      setCurrentStep(4);
    }, 2000);
  };

  const handleInputChange = (field, value) => {
    if (field?.includes('.')) {
      const [parent, child] = field?.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleComplete = async () => {
    try {
      // Save to Supabase if user is authenticated
      const { user } = useAuth();
      if (user) {
        await assessmentService?.saveAssessment(
          'roi_calculator',
          `ROI Calculator - ${formData?.contactInfo?.company || 'Assessment'}`,
          formData,
          results
        );
      }
      
      // Generate PDF
      generateDataStrategyPDF(formData, results);
      
      if (onComplete) {
        onComplete(results);
      }
      
      // Show success message
      alert('Your ROI report has been generated and saved!');
    } catch (error) {
      console.error('Error saving ROI assessment:', error);
      alert('Report generated, but there was an issue saving to your account.');
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Company Size"
                  options={companySizeOptions}
                  value={formData?.companySize}
                  onChange={(value) => handleInputChange('companySize', value)}
                  required
                />
                <Select
                  label="Industry"
                  options={industryOptions}
                  value={formData?.industry}
                  onChange={(value) => handleInputChange('industry', value)}
                  required
                />
                <Input
                  label="Annual Revenue (€)"
                  type="number"
                  placeholder="e.g., 10000000"
                  value={formData?.annualRevenue}
                  onChange={(e) => handleInputChange('annualRevenue', e?.target?.value)}
                  required
                />
                <Input
                  label="Data Team Size"
                  type="number"
                  placeholder="e.g., 5"
                  value={formData?.dataTeamSize}
                  onChange={(e) => handleInputChange('dataTeamSize', e?.target?.value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Current Data Capabilities</h3>
              <div className="space-y-4">
                <Select
                  label="Current Data Maturity Level"
                  options={maturityOptions}
                  value={formData?.currentDataMaturity}
                  onChange={(value) => handleInputChange('currentDataMaturity', value)}
                  required
                />
                <Input
                  label="Current Annual Data Costs (€)"
                  type="number"
                  placeholder="e.g., 500000"
                  value={formData?.currentDataCosts}
                  onChange={(e) => handleInputChange('currentDataCosts', e?.target?.value)}
                  description="Include technology, personnel, and infrastructure costs"
                />
                <Input
                  label="Average Decision-Making Delay (days)"
                  type="number"
                  placeholder="e.g., 7"
                  value={formData?.decisionMakingDelay}
                  onChange={(e) => handleInputChange('decisionMakingDelay', e?.target?.value)}
                  description="How long does it take to get data for critical decisions?"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Contact Information</h3>
              <p className="text-muted-foreground mb-6">
                We'll send your detailed ROI report to this email address.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  placeholder="John Smith"
                  value={formData?.contactInfo?.name}
                  onChange={(e) => handleInputChange('contactInfo.name', e?.target?.value)}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="john@company.com"
                  value={formData?.contactInfo?.email}
                  onChange={(e) => handleInputChange('contactInfo.email', e?.target?.value)}
                  required
                />
                <Input
                  label="Company Name"
                  type="text"
                  placeholder="Acme Corporation"
                  value={formData?.contactInfo?.company}
                  onChange={(e) => handleInputChange('contactInfo.company', e?.target?.value)}
                  required
                />
                <Input
                  label="Job Title"
                  type="text"
                  placeholder="Chief Data Officer"
                  value={formData?.contactInfo?.role}
                  onChange={(e) => handleInputChange('contactInfo.role', e?.target?.value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {isCalculating ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-primary mb-2">Calculating Your ROI...</h3>
                <p className="text-muted-foreground">
                  Analyzing your data strategy potential based on industry benchmarks
                </p>
              </div>
            ) : results && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-6">Your Data Strategy ROI Analysis</h3>
                
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-secondary mb-2">
                      {results?.annualROI?.toFixed(0)}%
                    </div>
                    <div className="text-sm text-muted-foreground">Annual ROI</div>
                  </div>
                  <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-accent mb-2">
                      {formatCurrency(results?.totalBenefits)}
                    </div>
                    <div className="text-sm text-muted-foreground">Total Annual Benefits</div>
                  </div>
                  <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-primary mb-2">
                      {results?.paybackPeriod?.toFixed(1)} mo
                    </div>
                    <div className="text-sm text-muted-foreground">Payback Period</div>
                  </div>
                </div>

                {/* Benefit Breakdown */}
                <div className="bg-surface rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-primary mb-4">Benefit Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Operational Efficiency Gains</span>
                      <span className="font-medium">{formatCurrency(results?.efficiencyGains)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Faster Decision Making</span>
                      <span className="font-medium">{formatCurrency(results?.decisionSpeedUp)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Risk Reduction</span>
                      <span className="font-medium">{formatCurrency(results?.riskReduction)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Cost Optimization</span>
                      <span className="font-medium">{formatCurrency(results?.costSavings)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between items-center font-semibold">
                      <span>Total Annual Benefits</span>
                      <span className="text-secondary">{formatCurrency(results?.totalBenefits)}</span>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="bg-white border border-secondary/20 rounded-lg p-6">
                  <h4 className="font-semibold text-primary mb-3">Recommended Next Steps</h4>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Check" size={16} color="var(--color-secondary)" />
                      <span className="text-sm">Download detailed ROI report with implementation roadmap</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Check" size={16} color="var(--color-secondary)" />
                      <span className="text-sm">Schedule consultation to validate assumptions</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Check" size={16} color="var(--color-secondary)" />
                      <span className="text-sm">Conduct data maturity assessment for detailed gap analysis</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="default"
                      iconName="Download"
                      iconPosition="left"
                      className="bg-secondary hover:bg-secondary/90"
                      onClick={handleComplete}
                    >
                      Download Full Report
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Book Consultation
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-primary">Data Strategy ROI Calculator</h2>
            <p className="text-sm text-muted-foreground">
              Calculate the potential return on your data strategy investment
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-surface">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-primary">
              Step {currentStep} of 4
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round((currentStep / 4) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div 
              className="bg-secondary h-2 rounded-full transition-all duration-300 progress-bar"
              style={{ width: `${(currentStep / 4) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-surface">
          <Button
            variant="outline"
            onClick={prevStep}
            iconName="ChevronLeft"
            iconPosition="left"
            className="hover:scale-105 hover:shadow-md focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
          >
            Previous
          </Button>
          
          <div className="flex space-x-2">
            {[1, 2, 3, 4]?.map((step) => (
              <div
                key={step}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  step === currentStep ? 'bg-secondary' : 
                  step < currentStep ? 'bg-secondary/50' : 'bg-border'
                }`}
              />
            ))}
          </div>

          {currentStep < 3 ? (
            <Button
              variant="default"
              onClick={nextStep}
              iconName="ChevronRight"
              iconPosition="right"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
            >
              Next Step
            </Button>
          ) : (
            <Button
              variant="default"
              onClick={calculateROI}
              loading={isCalculating}
              iconName="Calculator"
              iconPosition="right"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
            >
              Calculate ROI
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataStrategyCalculator;